import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Calendar, Flame, Crown, Timer } from "lucide-react";
import Podium from "../components/Podium";
import MiniBoard from "../components/MiniBoard";
import { leaderAvatarSrc } from "../lib/avatars";
import InfoMore from "../components/InfoMore";

function fmt(s) {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

/**
 * Daily Challenge page — one seeded board everyone gets per UTC day.
 * Shows your streak, today's best time, and the global top-50 board.
 */
export default function DailyChallenge() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [today, setToday] = useState(null);
  const [board, setBoard] = useState([]);
  const [prevWinner, setPrevWinner] = useState(null);

  const load = async () => {
    try {
      const [t1, t2, pw] = await Promise.all([
        api.get("/daily-challenge/today"),
        api.get("/daily-challenge/leaderboard?limit=50"),
        api.get("/daily-challenge/previous-winner"),
      ]);
      setToday(t1.data);
      setBoard(t2.data.leaders || []);
      setPrevWinner(pw.data || null);
      // Iter72 — surface auto-credited "missed" rewards. If a prior day's
      // +30 was lost (network race during /complete), the backend
      // self-heals on /today and reports the credit here so the player
      // gets a visible "🎁 yesterday's reward" toast.
      const backPaid = Number(t1.data?.back_paid_coins || 0);
      if (backPaid > 0) {
        toast.success(t("daily_challenge.back_paid_toast", { coins: backPaid }));
      }
    } catch (e) {
      setToday({});
    }
  };

  useEffect(() => { load(); }, []);

  // Re-fetch when the page is revisited (e.g. user comes back from
  // /play/daily/today after finishing the challenge). Without this the
  // leaderboard + streak still showed the pre-play state. Tester
  // complaint v34c: "Today's leaderboard should be visible after player
  // finish the game".
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  if (!today) return <div className="p-8 text-center text-[#5F7161]">…</div>;

  return (
    <div className="max-w-3xl mx-auto py-4 space-y-4">
      {/* Iter98 — "Yesterday's podium" — top-3 winners (gold/silver/bronze)
          with avatars + a mini silhouette of the exact board that was
          played. Emerald → charcoal gradient with a thin gold border. */}
      {prevWinner?.podium?.length > 0 && (
        <div
          data-testid="daily-prev-winner-card"
          className="relative rounded-2xl p-5 text-center overflow-hidden"
          style={{
            background: "linear-gradient(160deg, #3F5042 0%, #2C332A 60%, #1F2620 100%)",
            border: "1px solid rgba(182, 138, 60, 0.40)",
            boxShadow:
              "0 14px 30px -16px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)",
          }}
        >
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at 50% 25%, rgba(232,163,101,0.14), transparent 55%)",
            }}
          />

          <div className="relative flex flex-col items-center gap-4">
            <p className="text-[9px] uppercase tracking-[0.32em] text-[#E8A365]/80 font-medium">
              {t("daily_challenge.prev_winner_label")}
            </p>

            <Podium entries={prevWinner.podium} />

            {prevWinner.board?.footprint?.length > 0 && (
              <div className="flex flex-col items-center gap-1 pt-1">
                <MiniBoard footprint={prevWinner.board.footprint} size={72} />
                <p className="text-[8px] uppercase tracking-[0.24em] text-white/40">
                  {t("daily_challenge.board_played_label")}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="rounded-3xl bg-white border border-[#E6E2D8] p-5">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Calendar size={16} strokeWidth={1.5} className="text-[#5F7161]" />
          <p className="text-xs uppercase tracking-[0.25em] text-[#8AA48F]">{t("daily_challenge.label")}</p>
        </div>
        <h1 style={{ fontFamily: "Outfit, sans-serif" }} className="text-xl sm:text-2xl font-light mb-1.5 text-[#2C332A] text-center">
          {t("daily_challenge.title")}
        </h1>
        <div className="flex justify-center mb-4">
          <InfoMore text={t("daily_challenge.subtitle")} testid="daily-challenge-more" />
        </div>

        {today.difficulty && (
          <div className="flex justify-center mb-4 -mt-1">
            <span
              data-testid="dc-difficulty"
              className={`px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide ${
                today.difficulty === "hard"
                  ? "bg-[#B5564B]/12 text-[#B5564B]"
                  : today.difficulty === "medium"
                  ? "bg-[#E8A365]/15 text-[#B68A3C]"
                  : "bg-[#8AA48F]/15 text-[#5F7161]"
              }`}
            >
              {t(`daily_challenge.diff_${today.difficulty}`, today.difficulty)}
            </span>
          </div>
        )}

        <div className="grid grid-cols-3 gap-2 mb-4">
          <div data-testid="dc-streak" className="p-3 rounded-2xl bg-[#F7F5F0] border border-[#E6E2D8] min-w-0 flex flex-col items-center text-center">
            <Flame size={16} strokeWidth={1.5} className="text-[#E8A365] mb-1.5" />
            <p className="text-[9px] uppercase tracking-tight text-[#8AA48F] whitespace-nowrap">
              {t("daily_challenge.streak")}
            </p>
            <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-xl font-light tabular-nums">
              {today.streak || 0}
            </p>
          </div>
          <div data-testid="dc-best-today" className="p-3 rounded-2xl bg-[#F7F5F0] border border-[#E6E2D8] min-w-0 flex flex-col items-center text-center">
            <Timer size={16} strokeWidth={1.5} className="text-[#5F7161] mb-1.5" />
            <p className="text-[9px] uppercase tracking-tight text-[#8AA48F] whitespace-nowrap">
              {t("daily_challenge.best")}
            </p>
            <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-xl font-light tabular-nums">
              {fmt(today.best_time_sec)}
            </p>
          </div>
          <div data-testid="dc-reward" className="p-3 rounded-2xl bg-[#F7F5F0] border border-[#E6E2D8] min-w-0 flex flex-col items-center text-center">
            <Crown size={16} strokeWidth={1.5} className="text-[#B68A3C] mb-1.5" />
            <p className="text-[9px] uppercase tracking-tight text-[#8AA48F] whitespace-nowrap">
              {t("daily_challenge.reward")}
            </p>
            <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-xl font-light tabular-nums">
              +{today.reward_coins}
            </p>
          </div>
        </div>

        <Button
          data-testid="dc-play-btn"
          onClick={() => navigate("/play/daily/today")}
          className="w-full h-12 rounded-2xl bg-[#5F7161] hover:bg-[#4D5D4F] text-white text-base font-medium"
        >
          {today.already_completed ? t("daily_challenge.play_again") : t("daily_challenge.play_now")}
        </Button>
      </div>

      <div className="rounded-3xl bg-white border border-[#E6E2D8] p-5">
        <h2 className="text-sm uppercase tracking-[0.15em] text-[#8AA48F] mb-4">{t("daily_challenge.leaderboard")}</h2>
        {board.length === 0 && !today.best_time_sec && (
          <p className="text-sm text-[#2C332A]/60 py-4">{t("daily_challenge.no_finishers_yet")}</p>
        )}
        {board.length === 0 && today.best_time_sec != null && (
          <div data-testid="dc-row-self" className="flex items-center justify-between py-3 bg-[#F2F5EF] -mx-2 px-2 rounded-xl">
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-7 text-center text-sm text-[#5F7161] tabular-nums">1</span>
              <span className="text-sm text-[#2C332A] truncate">{t("leaderboard.you")}</span>
            </div>
            <span className="text-sm tabular-nums text-[#2C332A]/70">{fmt(today.best_time_sec)}</span>
          </div>
        )}
        <ul className="divide-y divide-[#E6E2D8]">
          {board.map((row) => (
            <li
              key={row.user_id}
              data-testid={`dc-row-${row.rank}`}
              className={`flex items-center justify-between py-3 ${row.is_me ? "bg-[#F2F5EF] -mx-2 px-2 rounded-xl" : ""}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-5 text-center text-sm text-[#5F7161] tabular-nums">{row.rank}</span>
                <div
                  className="w-8 h-8 rounded-full bg-cover bg-center ring-1 ring-[#E6E2D8] shrink-0"
                  style={{ backgroundImage: `url(${leaderAvatarSrc(row)})` }}
                />
                <span className="text-sm text-[#2C332A] truncate">{row.name}</span>
                {row.is_me && <span className="text-xs text-[#5F7161]">{t("leaderboard.you")}</span>}
              </div>
              <span className="text-sm tabular-nums text-[#2C332A]/70">{fmt(row.time_sec)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
